import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // Optimistic hydration — if a bearer token is in localStorage at mount,
  // assume the user is authenticated and let checkAuth() either confirm
  // (filling in the real shape) or clear it on 401. This eliminates the
  // initial-render race that previously redirected to '/' for any flow
  // that pre-seeds the token (E2E tests, Playwright, Postman-like).
  const _hasToken = typeof window !== "undefined" && !!localStorage.getItem("gf_session_token");
  const [user, setUser] = useState(_hasToken ? { _optimistic: true } : null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // Phase B (Feb 2026) — fallback to the customer-password session
      // realm. `/auth/me` only knows the staff/Google session token; a
      // browser cookie-authenticated customer will 401 there but 200
      // on `/customer-auth/status`. We normalize both into the same
      // `user` shape so the SPA doesn't care which realm signed in.
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/customer-auth/status`,
          { credentials: "include" },
        );
        if (res.ok) {
          const s = await res.json();
          const u = {
            user_id: s.customer_user_id,
            customer_user_id: s.customer_user_id,
            customer_id: s.customer_id,
            email: s.email,
            name: s.name || null,
            customer_name: s.customer_name || null,
            role: "customer",
            user_type: "customer",
            session_type: s.session_type,
            password_configured: s.password_configured,
            google_linked: s.google_linked,
          };
          setUser(u);
          return u;
        }
      } catch { /* fall through */ }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    // Try both realms — first the customer realm (needs CSRF), then
    // the staff endpoint. Either succeeds silently; failures are fine.
    try {
      const { customerAuth } = await import("./customerAuth");
      await customerAuth.logout();
    } catch { /* not a customer session */ }
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("gf_session_token");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, setUser, logout, refresh: checkAuth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
